// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Draws one page of a decoded TIFF to a canvas (enlarge-preview-annotate task 7.2). The same UTIF steps as
// `TiffView` (DocumentViewer) and `TiffThumb` (PageThumbnails), which move onto this helper in tasks 9.1 and 9.3.
import * as UTIF from 'utif';

import type { DecodedTiff } from './loadedDocument';

/**
 * Decodes IFD `page - 1` of `tiff` and draws it to `canvas` at the image's own pixel size. Returns that size.
 * Throws when the page is beyond the TIFF's IFDs or cannot be decoded.
 */
export function drawTiffPage(
  tiff: DecodedTiff,
  page: number,
  canvas: HTMLCanvasElement,
): { width: number; height: number } {
  const ifd = tiff.ifds[page - 1];
  if (!ifd) throw new Error(`TIFF has no page ${page}`);
  UTIF.decodeImage(tiff.buf, ifd);
  const rgba = UTIF.toRGBA8(ifd);
  const { width, height } = ifd;
  if (!(width > 0 && height > 0)) throw new Error('TIFF page has no pixels');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  ctx.putImageData(new ImageData(new Uint8ClampedArray(rgba), width, height), 0, 0);
  return { width, height };
}
